import { Match } from "@/models/match/Match";
import { getPlayersDb } from "@/utils/db/playersDb";
import { roundFloat } from "../math/math";

const STORE_NAME = "playerInfos";
const CACHE_DURATION_HOURS = 48;

/**
 * Renvoi un nouveau match en ajoutant la note moyenne des joueurs
 * dont le match n'a pas encore commencé
 * @param {Match} match le match à modifier
 * @param {*} championshipMatches matchs du championnat
 * @param {Function} getPlayerInfos la récupération des infos d'un joueur depuis l'API
 * @returns {Match} le match avec les moyennes pour les joueurs
 */
const setMatchPlayersAverageRating = async (match, championshipMatches, getPlayerInfos) => {
  const matchCopy = new Match(match);
  const notStartedClubIds = getNotStartedClubIds(championshipMatches);
  const playersToRate = getPlayersToRate(matchCopy, notStartedClubIds);

  if (playersToRate.length === 0) {
    return matchCopy;
  }

  const localPlayersInfos = await getLocalPlayersInfos(playersToRate.map(player => player.playerId));
  const fetchedPlayersInfos = new Map();

  // allSettled : un joueur en échec (API ou base locale) ne doit jamais
  // empêcher le calcul du match, sous peine de bloquer tout le classement
  await Promise.allSettled(playersToRate.map(async (player) => {
    const playerInfos = localPlayersInfos.get(player.playerId)
      || await fetchPlayerInfos(player, match, getPlayerInfos, fetchedPlayersInfos);
    setPlayerAverageRating(player, playerInfos);
  }));

  await setLocalPlayersInfos(fetchedPlayersInfos);

  return matchCopy;
};

/**
 * Renvoi les clubs dont le match de championnat n'a pas encore commencé
 * @param {*} championshipMatches matchs du championnat
 * @returns {Set} les ids des clubs concernés
 */
const getNotStartedClubIds = (championshipMatches) => {
  const notStartedClubIds = new Set();

  Object.values(championshipMatches)
    .filter(match => match.matchStatus !== "postponed" && (! match.period || match.period === "preMatch"))
    .forEach((championshipMatch) => {
      notStartedClubIds.add(championshipMatch.home.clubId);
      notStartedClubIds.add(championshipMatch.away.clubId);
    });

  return notStartedClubIds;
};

/**
 * Renvoi les joueurs des 2 équipes ayant besoin de leur note moyenne
 * @param {Match} match le match
 * @param {Set} notStartedClubIds les clubs dont le match n'a pas commencé
 * @returns {Player[]} les joueurs à noter
 */
const getPlayersToRate = (match, notStartedClubIds) => {
  return [match.homeTeam, match.awayTeam]
    .flatMap(team => [...team.pitchPlayers, ...team.benchPlayers])
    .filter(player => notStartedClubIds.has(player.clubId));
};

const fetchPlayerInfos = async (player, match, getPlayerInfos, fetchedPlayersInfos) => {
  try {
    const playerInfos = await getPlayerInfos(player.playerId, match.championshipId, match.championshipSeason);
    fetchedPlayersInfos.set(player.playerId, playerInfos);
    return playerInfos;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Impossible de récupérer les notes de ${player.lastName}`, error);
  }
};

const setPlayerAverageRating = (player, playerInfos) => {
  if (! playerInfos?.averageRating) {
    return;
  }
  player.rating = roundFloat(playerInfos.averageRating, 1);
  player.isAverageRating = true;
};

/**
 * Lit en une seule transaction les infos encore valides des joueurs demandés
 * @param {String[]} playerIds les ids des joueurs
 * @returns {Promise<Map>} les infos trouvées, par id de joueur
 */
const getLocalPlayersInfos = async (playerIds) => {
  const localPlayersInfos = new Map();

  try {
    const db = await getPlayersDb();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const storedPlayersInfos = await Promise.all(playerIds.map(playerId => transaction.store.get(playerId)));
    await transaction.done;

    playerIds.forEach((playerId, index) => {
      const storedPlayerInfos = storedPlayersInfos[index];
      if (storedPlayerInfos && ! isOutdated(storedPlayerInfos.updatedAt)) {
        localPlayersInfos.set(playerId, storedPlayerInfos.data);
      }
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Impossible de lire les notes en base locale", error);
  }

  return localPlayersInfos;
};

/**
 * Enregistre en une seule transaction les infos récupérées depuis l'API
 * @param {Map} playersInfos les infos à enregistrer, par id de joueur
 */
const setLocalPlayersInfos = async (playersInfos) => {
  if (playersInfos.size === 0) {
    return;
  }

  try {
    const db = await getPlayersDb();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const updatedAt = Date.now();
    playersInfos.forEach((data, playerId) => {
      transaction.store.put({
        data,
        updatedAt,
      }, playerId);
    });
    await transaction.done;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Impossible d'enregistrer les notes en base locale", error);
  }
};

const isOutdated = (updatedAt) => {
  const differenceInHours = (Date.now() - updatedAt) / (1000 * 60 * 60);
  return differenceInHours >= CACHE_DURATION_HOURS;
};

export { setMatchPlayersAverageRating };